import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/rentApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Home } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Connexion réussie");
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1585128792103-0b591f96512e" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-primary/60" />
        <div className="absolute bottom-10 left-10 text-white max-w-sm">
          <h2 className="font-heading text-3xl font-bold">Bon retour</h2>
          <p className="mt-2 text-white/80">Pilotez votre patrimoine locatif meublé depuis un seul espace.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="w-8 h-8 rounded-lg bg-primary text-primary-foreground grid place-items-center"><Home className="w-4 h-4" /></div>
            <span className="font-heading font-bold text-xl">Rent</span>
          </Link>
          <h1 className="font-heading text-2xl font-bold">Connexion</h1>
          <p className="text-sm text-muted-foreground mt-1">Accédez à votre espace de gestion.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Mot de passe</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="login-submit">
              {loading ? "Connexion…" : "Se connecter"}
            </Button>
          </form>

          <div className="relative my-6 text-center">
            <span className="text-xs text-muted-foreground bg-background px-3 relative z-10">ou</span>
            <div className="absolute inset-x-0 top-1/2 h-px bg-border" />
          </div>
          <Button variant="outline" className="w-full rounded-full" onClick={googleLogin} data-testid="google-login-btn">
            Continuer avec Google
          </Button>

          <p className="text-sm text-muted-foreground mt-6 text-center">
            Pas encore de compte ? <Link to="/register" className="text-foreground font-medium underline">Créer un compte</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
